import { useState } from 'react';

import { PARTNER_ROUTES } from '@repo/common';
import { DataTestIds, NotificationType } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { AvatarButton } from '@repo/ui/components/elements/AvatarButton/AvatarButton';
import { HeaderNotificationsSection } from '@repo/ui/components/HeaderNotificationsSection/HeaderNotificationsSection';
import type { TabType } from '@repo/ui/components/HeaderNotificationsSection/NotificationTabs';
import { Header } from '@repo/ui/fixtures/header/components/Header/Header';
import { LanguageSwitcher } from '@repo/ui/i18n/language-switcher';

import type { NotificationFilters } from '../hooks/use-in-app-notifications';
import { useInAppNotifications } from '../hooks/use-in-app-notifications';

type FilterType = 'all' | 'unread-only' | 'archived';

function getFiltersForTab(selectedTab: TabType): Partial<NotificationFilters> {
  if (selectedTab === 'All') return {};
  if (selectedTab === 'Alerts') {
    // The lifecycle-trigger materialiser writes SYSTEM-typed in-app
    // notifications, so partner-ops alerts (renewal reminders, seat
    // urgency) land in this tab.
    return { type: NotificationType.SYSTEM };
  }
  // 'Scenarios' tab is hardcoded into the shared `HeaderNotificationsSection`
  // — partner managers won't typically have ACHIEVEMENT notifications, but
  // matching web/admin's mapping keeps the list consistent with the badge
  // count instead of falling through to an unfiltered "All" query.
  return { type: NotificationType.ACHIEVEMENT };
}

function getFiltersForNotificationFilter(filterType: FilterType): Partial<NotificationFilters> {
  // Return only the keys we actually want to constrain on — undefined-value
  // keys still inflate `Object.keys(filters).length`, which trips the
  // `useInAppNotifications` hook's `hasFilters` check and forces refetches
  // for what is logically a no-op filter.
  if (filterType === 'unread-only') return { isRead: false };
  if (filterType === 'archived') return { isArchived: true };
  return {};
}

export function PartnerHeader() {
  // `Header` renders both desktop and mobile slots simultaneously, so we
  // call `useInAppNotifications` ONCE here and feed both slots from the
  // same state. Mounting a wrapping component into each slot would run
  // the hook twice — duplicate queries, duplicate polling, duplicate
  // subscriptions, duplicate cache churn on every notification event.
  const [selectedTab, setSelectedTab] = useState<TabType>('All');
  const [filterType, setFilterType] = useState<FilterType>('all');

  const tabFilters = getFiltersForTab(selectedTab);
  const notificationFilters = getFiltersForNotificationFilter(filterType);
  const combinedFilters: NotificationFilters = { ...tabFilters, ...notificationFilters };

  const {
    notifications,
    unreadCount,
    tabCounts,
    loadMore,
    hasMore,
    markAllAsRead,
    archiveAll,
    markAsRead,
    markAsUnread,
    archive,
    unarchive,
    refetch,
  } = useInAppNotifications(combinedFilters);

  const headerActions = (
    <SDSFlexbox alignItems="center" gap="button-medium">
      <HeaderNotificationsSection
        notifications={notifications}
        unreadCount={unreadCount}
        tabCounts={tabCounts}
        onOpen={() => void refetch()}
        onTabChange={setSelectedTab}
        onFilterChange={setFilterType}
        onLoadMore={loadMore}
        hasMore={hasMore}
        onMarkAllAsRead={markAllAsRead}
        onArchiveAll={archiveAll}
        onMarkAsRead={markAsRead}
        onMarkAsUnread={markAsUnread}
        onArchive={archive}
        onUnarchive={unarchive}
        dataTestId={DataTestIds.PARTNER.NOTIFICATIONS.TABS}
        triggerDataTestId={DataTestIds.PARTNER.NOTIFICATIONS.TRIGGER}
        notificationItemDataTestId={DataTestIds.PARTNER.NOTIFICATIONS.ITEM}
        tabDataTestIds={{
          All: `${DataTestIds.PARTNER.NOTIFICATIONS.TABS}-all`,
          Alerts: DataTestIds.PARTNER.NOTIFICATIONS.TAB_ALERTS,
          Scenarios: `${DataTestIds.PARTNER.NOTIFICATIONS.TABS}-scenarios`,
        }}
      />
      <LanguageSwitcher />
      <AvatarButton profilePath={PARTNER_ROUTES.PARTNER_SETTINGS} />
    </SDSFlexbox>
  );

  return (
    <Header
      navLinks={[]}
      logoHref={PARTNER_ROUTES.PARTNER_DASHBOARD}
      postNavLinkDesktopContent={headerActions}
      postNavLinkMobileContent={headerActions}
    />
  );
}
